<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import type { Organisation } from '@/types/auth'
import type { OrganisationWriteError } from '@/types/organisation-settings'

const { t } = useI18n()

const props = defineProps<{
  organisation: Organisation
  loading: boolean
  logoErrors: string[]
  logoActionError: OrganisationWriteError | null
  isOwner?: boolean
  // Passed via shared RouterView slot — unused by this panel
  orgId?: number
  formErrors?: Record<string, string[]>
  settingsError?: OrganisationWriteError | null
  saved?: boolean
}>()

const emit = defineEmits<{
  uploadLogo: [file: File]
  deleteLogo: []
  // Declared to satisfy the shared RouterView slot's event listeners
  save: [payload: Record<string, unknown>]
  cancel: []
}>()

const showDeleteLogoConfirm = ref<boolean>(false)
const logoInputRef = ref<HTMLInputElement | null>(null)

function handleLogoUpload(event: Event): void {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  emit('uploadLogo', file)
  // Reset file input so the same file can be re-selected
  if (logoInputRef.value) {
    logoInputRef.value.value = ''
  }
}

function handleDeleteLogo(): void {
  showDeleteLogoConfirm.value = false
  emit('deleteLogo')
}
</script>

<template>
  <div v-if="isOwner !== false" class="rounded-2xl border border-slate-700 bg-slate-800/50 p-6">
    <h2 class="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-4">
      {{ t('organizer.organisation.logo') }}
    </h2>

    <!-- Current logo or placeholder -->
    <div class="flex items-start gap-4 mb-4">
      <div class="shrink-0">
        <img
          v-if="organisation.logo_path"
          :src="organisation.logo_path"
          :alt="organisation.name"
          class="w-24 h-24 rounded-xl object-contain bg-slate-900 border border-slate-700 p-2"
        />
        <div
          v-else
          class="w-24 h-24 rounded-xl bg-slate-900 border border-slate-700 flex items-center justify-center"
        >
          <svg class="w-10 h-10 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <!-- Non-field write error (upload or delete): 403 (unverified email / not the
             owner / forbidden) or a genuinely transient 500. Never transient for a 403. -->
        <div v-if="logoActionError" class="text-xs text-red-400">
          {{ t(logoActionError.key) }}
          <RouterLink
            v-if="logoActionError.kind === 'email_unverified'"
            :to="{ name: 'organizer.verify-email' }"
            class="ml-1 font-semibold underline hover:text-red-300"
          >
            {{ t('organizer.organisation.errors.email_unverified_link') }}
          </RouterLink>
        </div>

        <!-- Logo field validation error (422) -->
        <p v-if="logoErrors.length" class="text-xs text-red-400">{{ logoErrors[0] }}</p>

        <!-- Upload button -->
        <label class="inline-flex items-center gap-2 cursor-pointer bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          {{ t('organizer.organisation.upload_logo') }}
          <input
            ref="logoInputRef"
            type="file"
            accept=".jpg,.jpeg,.png,.svg,.webp"
            class="sr-only"
            :disabled="loading"
            @change="handleLogoUpload"
          />
        </label>

        <!-- Delete button -->
        <button
          v-if="organisation.logo_path"
          type="button"
          class="inline-flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-semibold rounded-lg px-4 py-2 text-sm border border-red-500/30 transition-colors"
          :disabled="loading"
          @click="showDeleteLogoConfirm = true"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          {{ t('organizer.organisation.delete_logo') }}
        </button>
      </div>
    </div>

    <!-- Loading spinner for logo operations -->
    <div v-if="loading && logoErrors.length === 0" class="flex items-center gap-2 text-xs text-slate-400">
      <svg class="animate-spin h-3 w-3 text-emerald-400" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <span>{{ t('common.loading') }}</span>
    </div>
  </div>

  <ConfirmDialog
    :open="showDeleteLogoConfirm"
    :title="t('organizer.organisation.delete_logo')"
    :body="t('organizer.organisation.delete_logo_confirm')"
    :confirm-label="t('common.delete')"
    :loading="loading"
    @update:open="showDeleteLogoConfirm = false"
    @confirm="handleDeleteLogo"
  />
</template>
